import * as z from "zod";
import DRIVER_DOCS from "@/components/Delivery/utils/driver-docs-constants";
import type { GetDriverByIdQuery } from "@/libs/generated/operations";

import type { Doc } from "../common/docs";

export const VehicleNumberEnum = z.enum([
  "LESS_THAN_3",
  "BETWEEN_3_5",
  "BETWEEN_5_10",
  "LESS_THAN_10",
  "BETWEEN_10_20",
  "BETWEEN_20_50",
  "MORE_THAN_10",
  "MORE_THAN_50",
]);

export const KycLevel = z.enum(["LIGHT", "REGULAR"]);

export const UboDeclarationStatus = z.enum([
  "CREATED",
  "VALIDATION_ASKED",
  "VALIDATED",
  "INCOMPLETE",
  "REFUSED",
]);

export const SubscriptionStatus = z.enum([
  "NONE",
  "PENDING",
  "FAILED",
  "CANCELLED",
  "ACTIVE",
  "EXPIRED",
]);

export const BusinessStatus = z.enum(["COMPANY", "DRIVER"]);

export const DriverFormSchema = z.object({
  id: z.number(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  birthDate: z.coerce.date().optional(),
  drivingLicenseDate: z.coerce.date().optional(),
  drivingPermitDueDate: z.coerce.date().optional(),
  drivingPermitNumber: z.string().optional(),
  business_status: BusinessStatus.optional(),
  iban: z.string().optional(),
  company: z.string().optional(),
  siret: z.string().optional(),
  truck_ownership_status: z.boolean().optional(),
  wgarage: z.boolean().optional(),
  insurance_expiration_date: z.coerce.date().optional(),
  vigilance_issue_date: z.coerce.date().optional(),
  kbis_issue_date: z.coerce.date().optional(),
  address: z
    .object({
      id: z.number().nullable().optional(),
      address: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      postalCode: z.string().nullable().optional(),
      addressPrecision: z.string().nullable().optional(),
    })
    .optional(),
  docs: z
    .array(
      z.object({
        label: z.string(),
        key: z.string(),
        type: z.string(),
        url: z.string(),
        metadata: z
          .object({
            id: z.string(),
          })
          .optional(),
      })
    )
    .optional(),
});

export type DriverFormSchemaType = z.infer<typeof DriverFormSchema>;

export function GetDriverFormDefaultValues(driverData?: GetDriverByIdQuery["getDriverById"]) {
  return {
    id: driverData?.id || 0,
    lastName: driverData?.lastName || "",
    firstName: driverData?.firstName || "",
    birthDate: driverData?.birthDate || undefined,
    phoneNumber: driverData?.phoneNumber || "",
    email: driverData?.email || "",
    address: {
      id: driverData?.address?.id || 0,
      address: driverData?.address?.address || "",
      city: driverData?.address?.city || "",
      postalCode: driverData?.address?.postalCode || "",
      addressPrecision: driverData?.address?.addressPrecision || "",
    },
    company: driverData?.company || "",
    siret: driverData?.siret || "",
    drivingLicenseDate: driverData?.drivingLicenseDate || undefined,
    drivingPermitDueDate: driverData?.drivingPermitDueDate || undefined,
    drivingPermitNumber: driverData?.drivingPermitNumber || "",
    iban: driverData?.iban || "",
    truck_ownership_status: driverData?.truck_ownership_status ?? undefined,
    wgarage: driverData?.wgarage ?? undefined,
    insurance_expiration_date: driverData?.insurance_expiration_date || undefined,
    vigilance_issue_date: driverData?.vigilance_issue_date || undefined,
    kbis_issue_date: driverData?.kbis_issue_date || undefined,
    docs: DRIVER_DOCS.map((doc) => {
      const existingDoc = driverData?.docs?.find((d: Doc) => d.key === doc.key);
      return {
        ...doc,
        url: existingDoc?.url || "",
        key: existingDoc?.key || "",
        metadata: {
          id: existingDoc?.metadata?.id || doc.id,
        },
      };
    }),
  };
}
